import {
  ActionIcon,
  Box,
  Button,
  Loader,
  SegmentedControl,
  Text,
  Tooltip,
} from "@mantine/core";
import { Calendar } from "@mantine/dates";
import { useElementSize, useMediaQuery } from "@mantine/hooks";
import {
  IconChevronLeft,
  IconChevronRight,
  IconHelpCircle,
  IconPencil,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { type CSSProperties, useCallback, useMemo, useState } from "react";
import { DSS_DASHBOARD_URL } from "src/utils/dss/dss.constants";

import { LEGEND_ITEMS } from "./DSSCalendar.constants";
import styles from "./DSSCalendar.module.scss";
import {
  type DSSCalendarServiceType,
  type DSSCalendarView,
} from "./DSSCalendar.types";
import { getEventsForDate, hasEvents } from "./DSSCalendar.utils";
import { useDSSCalendarEvents } from "./useDSSCalendarEvents";

const serviceDotClass: Record<DSSCalendarServiceType, string> = {
  seaService: styles.dotSeaService,
  watchkeeping: styles.dotWatchkeeping,
  standby: styles.dotStandby,
  yard: styles.dotYard,
  portStay: styles.dotPortStay,
};

const SOURCE_ITEMS = [
  {
    label: "Green check",
    description: "AIS-classified sea-service type.",
  },
  {
    label: "Amber warning",
    description: "AIS row stuck on Onboard (ambiguous — edit the day).",
  },
  {
    label: "Pencil",
    description: "Vessel admin on the official log.",
  },
  {
    label: "Pending",
    description: "Your personal claim.",
  },
];

const WEEKDAYS_ROW_HEIGHT = 24;
const MONTH_GRID_SPACING = 4;
const YEAR_GRID_SPACING = 8;
const YEAR_GRID_ROWS = 4;
const MIN_DAY_HEIGHT = 40;

const getWeeksInMonth = (date: string): number => {
  const first = dayjs(date).startOf("month");
  const mondayOffset = (first.day() + 6) % 7;
  return Math.ceil((mondayOffset + first.daysInMonth()) / 7);
};

export const DSSCalendar = () => {
  const [displayDate, setDisplayDate] = useState(() =>
    dayjs().format("YYYY-MM-DD"),
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [view, setView] = useState<DSSCalendarView>("month");

  const { ref: bodyRef, height: bodyHeight } = useElementSize();
  const isStacked = useMediaQuery("(max-width: 900px)");

  const month = dayjs(displayDate).format("YYYY-MM");
  const { events, isLoading } = useDSSCalendarEvents(month);

  const headerLabel = useMemo(
    () =>
      view === "month"
        ? dayjs(displayDate).format("MMMM YYYY").toUpperCase()
        : dayjs(displayDate).format("YYYY"),
    [displayDate, view],
  );

  const navigate = useCallback(
    (direction: -1 | 1) => {
      const unit = view === "month" ? "month" : "year";
      setDisplayDate(
        dayjs(displayDate).add(direction, unit).format("YYYY-MM-DD"),
      );
    },
    [displayDate, view],
  );

  const goToToday = useCallback(() => {
    setDisplayDate(dayjs().format("YYYY-MM-DD"));
    setSelectedDate(dayjs().format("YYYY-MM-DD"));
  }, []);

  const renderDay = useCallback(
    (date: string) => {
      const dayEvents = getEventsForDate(events, date);

      return (
        <div className={styles.dayContent}>
          <span className={styles.dayNumber}>{dayjs(date).date()}</span>
          {dayEvents.length > 0 && (
            <div className={styles.eventDots}>
              {dayEvents.slice(0, 3).map(event => (
                <span
                  key={event.id}
                  className={`${styles.eventDot} ${serviceDotClass[event.type]}`}
                />
              ))}
            </div>
          )}
        </div>
      );
    },
    [events],
  );

  const bodyStyle = useMemo(() => {
    if (isStacked || bodyHeight === 0) {
      return undefined;
    }
    const weeks = getWeeksInMonth(displayDate);
    const dayHeight = Math.floor(
      (bodyHeight - WEEKDAYS_ROW_HEIGHT - (weeks + 2) * MONTH_GRID_SPACING) /
        weeks,
    );
    const monthHeight = Math.floor(
      (bodyHeight - (YEAR_GRID_ROWS + 1) * YEAR_GRID_SPACING) / YEAR_GRID_ROWS,
    );
    return {
      "--dss-day-height": `${Math.max(dayHeight, MIN_DAY_HEIGHT)}px`,
      "--dss-month-height": `${Math.max(monthHeight, MIN_DAY_HEIGHT)}px`,
    } as CSSProperties;
  }, [bodyHeight, displayDate, isStacked]);

  const getDayProps = useCallback(
    (date: string) => {
      const dayHasEvents = hasEvents(events, date);
      const isSelected = selectedDate === date;

      return {
        className: [
          dayHasEvents ? styles.dayWithEvents : "",
          isSelected ? styles.daySelected : "",
        ]
          .filter(Boolean)
          .join(" "),
        "data-has-events": dayHasEvents || undefined,
        onClick: () => setSelectedDate(date),
      };
    },
    [events, selectedDate],
  );

  return (
    <div className={styles.DSSCalendarRoot}>
      {isLoading ? (
        <Box className={styles.loader}>
          <Loader />
        </Box>
      ) : (
        <div className={styles.layout}>
          <div className={styles.calendarColumn}>
            <div className={styles.warningBanner}>
              <IconPencil size={14} className={styles.warningIcon} />
              <Text className={styles.warningText}>
                VIEW ONLY – CALENDAR CANNOT BE EDITED. CONTACT YOUR VESSEL ADMIN
                TO UPDATE SEA SERVICE.
              </Text>
            </div>

            <div className={styles.calendarCard}>
              <div className={styles.calendarToolbar}>
                <Text className={styles.monthTitle}>{headerLabel}</Text>

                <div className={styles.toolbarControls}>
                  <SegmentedControl
                    className={styles.viewToggle}
                    value={view}
                    onChange={value => setView(value as DSSCalendarView)}
                    data={[
                      { label: "MONTH", value: "month" },
                      { label: "YEARLY OVERVIEW", value: "year" },
                    ]}
                  />

                  <div className={styles.navGroup}>
                    <ActionIcon
                      variant="default"
                      size={36}
                      className={styles.navButton}
                      aria-label="Previous"
                      onClick={() => navigate(-1)}>
                      <IconChevronLeft size={16} />
                    </ActionIcon>
                    <Button
                      variant="light"
                      size="sm"
                      className={styles.todayButton}
                      onClick={goToToday}>
                      TODAY
                    </Button>
                    <ActionIcon
                      variant="default"
                      size={36}
                      className={styles.navButton}
                      aria-label="Next"
                      onClick={() => navigate(1)}>
                      <IconChevronRight size={16} />
                    </ActionIcon>
                  </div>
                </div>
              </div>

              <Text className={styles.subtext}>
                YOU CAN ONLY LOG DAYS WITHIN YOUR SERVICE PERIODS. GAPS BETWEEN
                PERIODS ARE LEAVE AND ARE GREYED OUT.{" "}
                <a
                  className={styles.learnMore}
                  href={DSS_DASHBOARD_URL}
                  target="_blank"
                  rel="noopener noreferrer">
                  Learn more
                </a>
              </Text>

              <div className={styles.calendarBody}>
                <div
                  ref={bodyRef}
                  className={styles.calendarBodyInner}
                  style={bodyStyle}>
                  <Calendar
                    date={displayDate}
                    onDateChange={value => {
                      if (value) {
                        setDisplayDate(value);
                      }
                    }}
                    level={view}
                    onLevelChange={level => setView(level as DSSCalendarView)}
                    maxLevel="year"
                    minLevel="month"
                    highlightToday
                    firstDayOfWeek={1}
                    weekdayFormat="ddd"
                    hideOutsideDates
                    classNames={{
                      calendarHeader: styles.calendarHeaderHidden,
                      levelsGroup: styles.levelsGroup,
                      weekday: styles.weekday,
                      day: styles.day,
                      month: styles.month,
                      monthCell: styles.monthCell,
                      monthsList: styles.monthsList,
                      monthsListCell: styles.monthsListCell,
                      monthsListControl: styles.monthsListControl,
                    }}
                    getDayProps={getDayProps}
                    renderDay={renderDay}
                  />
                </div>
              </div>
            </div>
          </div>

          <aside className={styles.sidebar}>
            <div className={styles.selectionPanel}>
              <div className={styles.selectionHeader}>
                <Text className={styles.selectionTitle}>SELECTION</Text>
                <Tooltip
                  label="Legend for sea service day types"
                  withArrow
                  position="left">
                  <ActionIcon
                    variant="default"
                    size={36}
                    className={styles.selectionHelp}
                    aria-label="Legend help">
                    <IconHelpCircle size={16} />
                  </ActionIcon>
                </Tooltip>
              </div>

              <ul className={styles.legendList}>
                {LEGEND_ITEMS.map(item => (
                  <li key={item.type} className={styles.legendItem}>
                    <span
                      className={`${styles.legendDot} ${serviceDotClass[item.type]}`}
                    />
                    <Text className={styles.legendLabel}>{item.label}</Text>
                  </li>
                ))}
              </ul>

              <div className={styles.sourceLegend}>
                <Text className={styles.sourceTitle}>SOURCE</Text>
                <ul className={styles.sourceList}>
                  {SOURCE_ITEMS.map(({ label, description }) => (
                    <li key={label}>
                      <Text className={styles.sourceText}>
                        <strong>{label}</strong> {description}
                      </Text>
                    </li>
                  ))}
                </ul>
                <Text className={styles.sourceNote}>
                  Click a day for details. On claimed vessels, Dashboard career
                  totals and report PDFs use this same merged calendar (official
                  log plus your entries, with AIS Premium window rules).
                </Text>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};
